import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, firstValueFrom } from 'rxjs';
import { ComputerService } from 'src/app/computer/computer.service';
import { LabelPrintComponent } from 'src/app/computer/label-print/label-print.component';
import { PrintCellComponent } from 'src/app/computer/print-cell/print-cell.component';
import { StocktakingV2Service } from 'src/app/docs/stocktaking-v2/stocktaking-v2.service';
import {
  StocktakingV2Doc,
  StocktakingV2ShelfSessionItem
} from 'src/app/docs/stocktaking-v2/stocktaking-v2.types';
import { ProductService } from 'src/app/product/product.service';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { environment } from 'src/environments/environment';
import {
  BarcodeResolution,
  BarcodeResolvedProductCandidate,
  BarcodeTecDocCandidate,
  Product,
  ProductCellBalance,
  User,
  Warehouse,
  WarehouseCell
} from 'src/typings';
import { WarehouseService } from '../warehouse.service';

type ScannerMode = 'PLACE' | 'PICK' | 'COUNT' | 'INFO';

type ScannerRecentAction = {
  id: string;
  mode: ScannerMode;
  cellCode: string;
  productLabel: string;
  qty: number;
  message: string;
  createdAt: number;
  success: boolean;
};

type ScannerCountItem = {
  barcode: string;
  product: BarcodeResolvedProductCandidate;
  qty: number;
  lastScannedAt: number;
};

type ScannerSessionSnapshot = {
  warehouseId?: number | null;
  mode: ScannerMode;
  currentCell?: WarehouseCell | null;
  currentProduct?: BarcodeResolvedProductCandidate | null;
  pendingProduct?: BarcodeResolvedProductCandidate | null;
  pendingProductBarcode?: string | null;
  qtyOverride?: number | null;
  countSessionItems: ScannerCountItem[];
  recentActions: ScannerRecentAction[];
};

const SESSION_STORAGE_KEY = 'warehouse-scanner-session-v2';

@Component({
  selector: 'app-warehouse-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss']
})
export class ScannerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('captureInput') captureInput?: ElementRef<HTMLInputElement>;
  @ViewChild('unknownSearchInput') unknownSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('countQtyInput') countQtyInput?: ElementRef<HTMLInputElement>;

  constructor(
    private readonly warehouseService: WarehouseService,
    private readonly stocktakingV2Service: StocktakingV2Service,
    private readonly productService: ProductService,
    private readonly computerService: ComputerService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly store: Store<AppState>,
    private readonly route: ActivatedRoute
  ) {
    this.subscriptions.add(
      this.store.subscribe((state) => {
        const defaultWarehouse = state.user.info?.defaultWarehouse;
        this.currentUserId = state.user.info?.id;
        this.currentUser = state.user.info;
        if (!this.warehouseControl.value && defaultWarehouse) {
          this.warehouseControl.setValue(defaultWarehouse);
        }
        if (!this.isStocktakingDocMode && !this.canUseAdhocCount && this.modeControl.value === 'COUNT') {
          this.modeControl.setValue('PLACE', { emitEvent: false });
        }
      })
    );

    this.subscriptions.add(
      this.modeControl.valueChanges.subscribe((value) => {
        if (value) {
          this.handleModeChange(value as ScannerMode);
        }
      })
    );

    this.subscriptions.add(
      this.warehouseControl.valueChanges.subscribe((warehouse) => {
        void this.handleWarehouseChange(warehouse as Warehouse | undefined);
      })
    );

    this.subscriptions.add(
      this.route.paramMap.subscribe((params) => {
        void this.handleRouteDocContext(Number(params.get('id') || 0));
      })
    );

    this.subscriptions.add(
      this.route.queryParamMap.subscribe((params) => {
        void this.applyRouteContext({
          warehouseId: Number(params.get('warehouse_id') || 0),
          productId: Number(params.get('product_id') || 0),
          cellId: Number(params.get('cell_id') || 0),
          mode: (params.get('mode') as ScannerMode | null) || undefined
        });
      })
    );

    this.subscriptions.add(
      this.productService.productGw.subscribe((products) => {
        const updated = products.find((product) => product.id === this.currentProduct?.id);
        if (updated) {
          this.applyRealtimeProductUpdate(updated);
        }
      })
    );

    void this.restoreSessionSnapshot();
  }

  warehouseControl = new UntypedFormControl();
  modeControl = new UntypedFormControl('PLACE');
  captureControl = new UntypedFormControl('');
  qtyControl = new UntypedFormControl(1);
  appliedQtyOverride = 1;
  unknownSearchControl = new UntypedFormControl('');
  countQtyControl = new UntypedFormControl(1);
  isMobileLayout = (globalThis.window?.innerWidth || 0) <= 900;
  showWarehouseSelector = false;
  captureKeyboardEnabled = false;

  currentCell?: WarehouseCell;
  currentProduct?: BarcodeResolvedProductCandidate;
  currentProductDetails?: Product;
  pendingProduct?: BarcodeResolvedProductCandidate;
  pendingProductBarcode = '';
  productBalances: ProductCellBalance[] = [];
  stocktakingDoc?: StocktakingV2Doc;
  stocktakingDocId?: number;
  labelPrinterWarning = '';
  currentUser?: User;
  loading = false;
  candidates: BarcodeResolvedProductCandidate[] = [];
  tecdocCandidates: BarcodeTecDocCandidate[] = [];
  tecdocCandidateLoading = false;
  tecdocCandidateError = '';
  pendingUnknownBarcode = '';
  searchResults: Product[] = [];
  showQtyOverride = false;
  manualProductSearchOpen = false;
  countEditingProductId?: number;
  pendingCellSwitch?: WarehouseCell;
  countSessionItems: ScannerCountItem[] = [];
  recentActions: ScannerRecentAction[] = [];
  scanSeries?: {
    barcode: string;
    mode: ScannerMode;
    cellCode: string;
    productLabel: string;
    qty: number;
  };
  readonly sessionMemory = new Map<string, BarcodeResolvedProductCandidate>();
  private productLabelLongPressTimer?: number;
  private suppressNextProductLabelClick = false;
  private cellLabelLongPressTimer?: number;
  private suppressNextCellLabelClick = false;

  private readonly subscriptions = new Subscription();
  private heartbeatTimer?: number;
  private scanBuffer = '';
  private lastWarehouseId?: number;
  private currentUserId?: number;
  private pendingRouteCellId?: number;

  ngAfterViewInit() {
    this.focusCaptureInput(true);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.stopStocktakingHeartbeat();
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent) {
    if (this.isCapturePaused) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (event.key === 'Enter') {
      const raw = this.scanBuffer.trim();
      this.scanBuffer = '';
      if (raw) {
        event.preventDefault();
        void this.processScan(raw);
      }
      return;
    }

    if (event.key === 'Escape') {
      this.scanBuffer = '';
      return;
    }

    if (event.key.length !== 1) {
      return;
    }

    this.scanBuffer += event.key;
    event.preventDefault();
  }

  get warehouse(): Warehouse | undefined {
    return this.warehouseControl.value as Warehouse;
  }

  get mode(): ScannerMode {
    return (this.modeControl.value as ScannerMode) || 'PLACE';
  }

  get isStocktakingDocMode(): boolean {
    return Number(this.stocktakingDocId || 0) > 0;
  }

  get stocktakingPrintWarehouseId(): number | null {
    return this.isStocktakingDocMode
      ? this.stocktakingDoc?.warehouse?.id ?? this.warehouse?.id ?? null
      : this.warehouse?.id ?? this.currentUser?.defaultWarehouse?.id ?? null;
  }

  get stocktakingLabelProfileMode() {
    return this.isStocktakingDocMode ? 'printer_target' as const : 'user_default' as const;
  }

  get isAdminUser(): boolean {
    return this.currentUser?.roles?.some((role) => role.description === 'admin') === true;
  }

  get canUseAdhocCount(): boolean {
    return this.isStocktakingDocMode || this.isAdminUser;
  }

  get sessionStorageKey(): string {
    return this.isStocktakingDocMode
      ? `stocktaking-v2-scanner-session-${this.stocktakingDocId}`
      : SESSION_STORAGE_KEY;
  }

  get stocktakingStatusLabel(): string {
    switch (this.stocktakingDoc?.status) {
      case 'COUNTING':
        return 'Документ в работе';
      case 'REVIEW':
        return 'Отчет готов';
      case 'APPLIED':
        return 'Результат применен';
      case 'CANCELLED':
        return 'Документ отменен';
      default:
        return 'Переучет документа';
    }
  }

  get isReadOnlyStocktakingDoc(): boolean {
    return this.isStocktakingDocMode && Boolean(this.stocktakingDoc) && this.stocktakingDoc?.status !== 'COUNTING';
  }

  get isCapturePaused(): boolean {
    return (
      this.isReadOnlyStocktakingDoc ||
      this.loading ||
      this.manualProductSearchOpen ||
      Boolean(this.pendingUnknownBarcode) ||
      Boolean(this.pendingCellSwitch)
    );
  }

  get hasCountSession(): boolean {
    return this.mode === 'COUNT' && Boolean(this.currentCell);
  }

  get currentInstruction(): string {
    if (this.isReadOnlyStocktakingDoc) {
      return 'Документ уже не находится в режиме пересчета';
    }
    if (!this.warehouse?.id) {
      return 'Сначала выберите склад';
    }
    if (this.pendingCellSwitch) {
      return `Полка ${this.currentCell?.code} не завершена. Выберите, что делать дальше`;
    }
    if (this.pendingUnknownBarcode && this.tecdocCandidates.length) {
      return 'Код найден в TecDoc. Выберите карточку для создания или привязки товара';
    }
    if (this.pendingUnknownBarcode && this.candidates.length) {
      return 'Код найден у нескольких товаров. Выберите нужный товар';
    }
    if (this.pendingUnknownBarcode) {
      return 'Код не найден. Найдите товар и привяжите его';
    }
    if (this.mode === 'INFO') {
      if (this.currentProduct) {
        return 'Карточка товара открыта. Сканируйте следующий товар для просмотра';
      }
      return 'Сканируйте штрихкод или ID товара для просмотра информации';
    }
    if (this.isStocktakingDocMode && !this.currentCell) {
      return 'Сканируйте полку для документа переучета';
    }
    if (!this.currentCell) {
      return 'Сканируйте полку';
    }
    if (this.pendingProduct) {
      return `Полка ${this.currentCell.code} активна. Товар готов к обработке`;
    }
    if (this.mode === 'COUNT') {
      return `Полка ${this.currentCell.code} активна. Сканируйте штрихкод или ID товара для переучета`;
    }
    if (this.mode === 'PICK') {
      return `Полка ${this.currentCell.code} активна. Сканируйте штрихкод или ID товара для снятия`;
    }
    return `Полка ${this.currentCell.code} активна. Сканируйте штрихкод или ID товара для размещения`;
  }

  get captureHint(): string {
    if (this.scanBuffer) {
      return this.scanBuffer;
    }
    if (this.pendingUnknownBarcode) {
      return this.pendingUnknownBarcode;
    }
    if (this.mode === 'INFO') {
      return 'Сканируйте штрихкод или ID товара';
    }
    if (!this.currentCell) {
      return 'Сканируйте код полки';
    }
    return 'Сканируйте штрихкод или ID товара';
  }

  get captureExample(): string {
    return this.mode === 'INFO' || this.currentCell
      ? 'Например: barcode или 12345'
      : 'Например: !A-01-02';
  }

  get modeTitle(): string {
    if (this.isStocktakingDocMode) {
      return 'Переучет документа';
    }
    if (this.mode === 'PLACE') {
      return 'Размещение';
    }
    if (this.mode === 'PICK') {
      return 'Снятие';
    }
    if (this.mode === 'INFO') {
      return 'Инфо';
    }
    return 'Переучет';
  }

  get stageToneLabel(): string {
    if (!this.currentCell) {
      return 'Шаг 1';
    }
    if (this.pendingProduct) {
      return 'Шаг 2';
    }
    return 'Готово к сканированию';
  }

  get activeCellLabel(): string {
    return this.currentCell?.code || 'Полка не выбрана';
  }

  get currentStageStatusLabel(): string {
    if (this.mode === 'INFO') {
      return this.warehouse?.description || 'Склад не выбран';
    }

    return this.currentCell?.code || 'Полка не выбрана';
  }

  get isStageStatusInactive(): boolean {
    return this.mode === 'INFO' ? !this.warehouse?.id : !this.currentCell;
  }

  get compactProductLabel(): string {
    if (!this.currentProduct) {
      return '';
    }
    return `${this.currentProduct.brand.description} ${this.currentProduct.article}`.trim();
  }

  get currentProductDisplayMeta(): string {
    const brand =
      this.currentProductDetails?.brand?.description || this.currentProduct?.brand?.description || '';
    const article = this.currentProductDetails?.article || this.currentProduct?.article || '';
    return `${brand} ${article}`.trim();
  }

  get currentProductDisplayName(): string {
    return (
      this.currentProductDetails?.description_ua ||
      this.currentProductDetails?.description ||
      this.currentProduct?.description ||
      ''
    ).trim();
  }

  get currentProductImageUrl(): string | null {
    const imageName = this.currentProductDetails?.images?.[0]?.name;
    return imageName ? `${environment.BACKEND}/image/${imageName}` : null;
  }

  get currentProductPurchasePrice(): number {
    return Number(this.currentProductDetails?.price ?? this.currentProduct?.price ?? 0);
  }

  get currentProductRetailPrice(): number {
    return Number(this.currentProductDetails?.retail ?? this.currentProduct?.retail ?? 0);
  }

  get currentProductTotalQty(): number {
    return this.round(
      this.productBalances.reduce((sum, row) => sum + Number(row.qty || 0), 0)
    );
  }

  get currentCountSessionItem(): ScannerCountItem | undefined {
    if (!this.currentProduct?.id) {
      return undefined;
    }

    return this.countSessionItems.find((item) => item.product.id === this.currentProduct?.id);
  }

  get currentExpectedShelfQty(): number {
    return this.getExpectedShelfQtyForProduct(this.currentProduct?.id);
  }

  get currentScannedQty(): number {
    return this.round(Number(this.currentCountSessionItem?.qty || 0));
  }

  get currentCountDelta(): number {
    return this.round(this.currentScannedQty - this.currentExpectedShelfQty);
  }

  get isCurrentCountEditing(): boolean {
    return Boolean(this.currentProduct?.id && this.countEditingProductId === this.currentProduct.id);
  }

  get currentCountDeltaLabel(): string {
    const delta = this.currentCountDelta;
    if (delta > 0) {
      return `+${delta}`;
    }

    return `${delta}`;
  }

  get countSessionTotalQty(): number {
    return this.round(
      this.countSessionItems.reduce((sum, item) => sum + Number(item.qty || 0), 0)
    );
  }

  get countSessionSummaryText(): string {
    if (!this.currentCell) {
      return 'Сначала сканируйте полку, затем товары.';
    }

    if (!this.countSessionItems.length) {
      return 'Можно сохранить пустую полку или продолжить сканирование.';
    }

    return `${this.countSessionItems.length} поз. на ${this.countSessionTotalQty} шт.`;
  }

  get canAlignCurrentCountToSystem(): boolean {
    return (
      this.mode === 'COUNT' &&
      Boolean(this.currentProduct?.id) &&
      this.currentScannedQty !== this.currentExpectedShelfQty
    );
  }

  getExpectedShelfQtyForProduct(productId?: number): number {
    if (!productId || !this.currentCell?.id) {
      return 0;
    }

    const directCellRow =
      this.productBalances.find(
        (row) => row.productId === productId && row.cellId === this.currentCell?.id && !row.isVirtual
      ) ||
      this.productBalances.find(
        (row) => row.productId === productId && row.cellId === this.currentCell?.id
      );

    return this.round(Number(directCellRow?.qty || 0));
  }

  getSearchProductImageUrl(product: Product): string | null {
    const imageName = product.images?.[0]?.name;
    return imageName ? `${environment.BACKEND}/image/${imageName}` : null;
  }

  getSearchProductName(product: Product): string {
    return (product.description_ua || product.description || '').trim() || 'Без названия';
  }

  getSearchProductBrand(product: Product): string {
    return (product.brand?.description || '').trim() || 'Без производителя';
  }

  getSearchProductArticle(product: Product): string {
    return (product.article || '').trim() || 'Без артикула';
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobileLayout = (globalThis.window?.innerWidth || 0) <= 900;
    if (!this.isMobileLayout) {
      this.showWarehouseSelector = false;
    }
  }

  get currentProductHintLabel(): string {
    return this.currentProduct?.cellHints?.join(', ') || '';
  }

  get currentProductLabel(): string {
    return this.currentProduct ? this.formatProductLabel(this.currentProduct) : 'Не выбран';
  }

  get pendingProductLabel(): string {
    return this.pendingProduct ? this.formatProductLabel(this.pendingProduct) : '';
  }

  get recentActionFeed(): ScannerRecentAction[] {
    return this.recentActions.slice(0, 6);
  }

  get activeQtyOverride(): number {
    return this.normalizeQtyValue(this.appliedQtyOverride);
  }

  get canFinishCountSession(): boolean {
    return this.mode === 'COUNT' && !!this.currentCell && !this.isCapturePaused;
  }

  onCaptureKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const raw = String(this.captureControl.value || '').trim();
      this.captureControl.setValue('');
      if (raw) {
        void this.processScan(raw);
      } else {
        this.focusCaptureInput(true);
      }
      return;
    }

    if (event.key === 'Escape' && String(this.captureControl.value || '').trim()) {
      event.preventDefault();
      this.captureControl.setValue('');
      this.focusCaptureInput(true);
    }
  }

  toggleQtyOverridePanel() {
    if (!this.showQtyOverride) {
      this.qtyControl.setValue(this.activeQtyOverride);
    }

    this.showQtyOverride = !this.showQtyOverride;

    if (!this.showQtyOverride) {
      this.focusCaptureInput();
    }
  }

  setQtyOverride(qty: number) {
    const normalizedQty = this.normalizeQtyValue(qty);
    this.appliedQtyOverride = normalizedQty;
    this.qtyControl.setValue(normalizedQty);
    this.showQtyOverride = false;
    this.persistSession();
    this.focusCaptureInput();
  }

  incrementQtyDraft(delta: number) {
    const current = this.normalizeQtyValue(this.qtyControl.value);
    const next = this.round(current + Number(delta || 0));
    this.qtyControl.setValue(this.normalizeQtyValue(next));
  }

  applyCustomQtyOverride() {
    const qty = this.normalizeQtyValue(this.qtyControl.value);
    this.appliedQtyOverride = qty;
    this.qtyControl.setValue(qty);
    this.showQtyOverride = false;
    this.persistSession();
    this.focusCaptureInput();
  }

  resetQtyOverride() {
    this.appliedQtyOverride = 1;
    this.qtyControl.setValue(1);
    this.showQtyOverride = false;
    this.persistSession();
    this.focusCaptureInput();
  }

  toggleCaptureKeyboard(event?: Event) {
    event?.stopPropagation();
    this.captureKeyboardEnabled = !this.captureKeyboardEnabled;

    if (!this.captureKeyboardEnabled) {
      this.focusCaptureInput(true);
      return;
    }

    const input = this.captureInput?.nativeElement;
    if (!input) {
      return;
    }

    input.focus({ preventScroll: true });
    const value = String(input.value || '');
    const position = value.length;
    input.setSelectionRange(position, position);
  }

  async printCurrentProductLabel() {
    if (!this.currentProduct?.id) {
      return this.notificationService.warning('Сначала отсканируйте товар.');
    }

    try {
      await this.quickPrintCurrentProductLabel();
    } catch (error) {
      this.handleOperationError(error, 'Не удалось отправить этикетку на печать');
    }
  }

  openCurrentProductLabelDialog() {
    if (!this.currentProduct?.id) {
      return this.notificationService.warning('Сначала отсканируйте товар.');
    }

    const dialogRef = this.dialog.open(LabelPrintComponent);
    dialogRef.componentInstance.productId = this.currentProduct.id;
    dialogRef.componentInstance.warehouseId = this.stocktakingPrintWarehouseId ?? undefined;
    dialogRef.componentInstance.labelProfileMode = this.stocktakingLabelProfileMode;
  }

  onProductLabelButtonContextMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.cancelProductLabelLongPress();

    if (this.suppressNextProductLabelClick) {
      return;
    }

    this.openCurrentProductLabelDialog();
  }

  onProductLabelButtonTouchStart() {
    this.cancelProductLabelLongPress();
    this.productLabelLongPressTimer = globalThis.window?.setTimeout(() => {
      this.suppressNextProductLabelClick = true;
      this.openCurrentProductLabelDialog();
    }, 450);
  }

  onProductLabelButtonTouchEnd() {
    this.cancelProductLabelLongPress();
  }

  async onProductLabelButtonClick(event?: Event) {
    if (this.suppressNextProductLabelClick) {
      event?.preventDefault();
      event?.stopPropagation();
      this.suppressNextProductLabelClick = false;
      return;
    }

    await this.printCurrentProductLabel();
  }

  async printCurrentCellLabel() {
    if (!this.currentCell?.code) {
      return this.notificationService.warning('Сначала отсканируйте ячейку.');
    }

    await this.quickPrintCurrentCellLabel();
  }

  onCellLabelButtonContextMenu(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.cancelCellLabelLongPress();

    if (this.suppressNextCellLabelClick) {
      return;
    }

    if (!this.currentCell?.code) {
      return this.notificationService.warning('Сначала отсканируйте ячейку.');
    }

    this.openCellLabelDialog(this.currentCell);
  }

  onCellLabelButtonTouchStart() {
    this.cancelCellLabelLongPress();
    this.cellLabelLongPressTimer = globalThis.window?.setTimeout(() => {
      this.suppressNextCellLabelClick = true;

      if (!this.currentCell?.code) {
        this.notificationService.warning('Сначала отсканируйте ячейку.');
        return;
      }

      this.openCellLabelDialog(this.currentCell);
    }, 450);
  }

  onCellLabelButtonTouchEnd() {
    this.cancelCellLabelLongPress();
  }

  async onCellLabelButtonClick(event?: Event) {
    if (this.suppressNextCellLabelClick) {
      event?.preventDefault();
      event?.stopPropagation();
      this.suppressNextCellLabelClick = false;
      return;
    }

    await this.printCurrentCellLabel();
  }

  async searchUnknownProducts() {
    const query = String(this.unknownSearchControl.value || '').trim();
    if (!query) {
      this.searchResults = [];
      return;
    }

    this.loading = true;
    try {
      const response = await firstValueFrom(
        this.productService.search({
          search_keys: query,
          default_warehouse_id: this.warehouse?.id,
          withStock: true,
          limit: 20
        })
      );
      this.searchResults = response.data;
    } catch (error) {
      this.handleOperationError(error, 'Не удалось найти товары');
    } finally {
      this.loading = false;
    }
  }

  openManualProductSearch(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();

    if (!this.warehouse?.id) {
      this.notificationService.warning('Сначала выберите склад.');
      return;
    }

    if (this.mode === 'COUNT' && !this.currentCell?.id) {
      this.notificationService.warning('Сначала отсканируйте полку.');
      return;
    }

    this.candidates = [];
    this.tecdocCandidates = [];
    this.tecdocCandidateError = '';
    this.pendingUnknownBarcode = '';
    this.searchResults = [];
    this.manualProductSearchOpen = true;
    this.unknownSearchControl.setValue('');
    this.persistSession();
    setTimeout(() => this.unknownSearchInput?.nativeElement.focus(), 0);
  }

  async bindBarcode(product: Product) {
    if (!this.pendingUnknownBarcode) return;

    this.loading = true;
    try {
      await firstValueFrom(
        this.productService.addBarcodeToProduct({
          product_id: product.id,
          barcode: this.pendingUnknownBarcode
        })
      );

      await this.consumeResolvedProduct(
        this.pendingUnknownBarcode,
        this.mapProductToCandidate(product)
      );
      this.notificationService.notice(`Штрихкод привязан к товару #${product.id}`);
    } catch (error) {
      this.handleOperationError(error, 'Не удалось привязать штрихкод');
    } finally {
      this.loading = false;
    }
  }

  async selectSearchResult(product: Product) {
    if (this.pendingUnknownBarcode) {
      await this.bindBarcode(product);
      return;
    }

    this.loading = true;
    try {
      const candidate = this.mapProductToCandidate(product);
      this.currentProduct = candidate;
      this.currentProductDetails = product;

      if (this.mode === 'INFO') {
        this.pendingProduct = undefined;
        this.pendingProductBarcode = '';
        await this.refreshBalances(product.id);
        this.pushRecentAction({
          mode: 'INFO',
          cellCode: this.warehouse?.description || '-',
          productLabel: this.formatProductLabel(candidate),
          qty: 1,
          message: 'Открыта карточка товара',
          success: true
        });
        this.scanSeries = {
          barcode: `product:${product.id}`,
          mode: 'INFO',
          cellCode: this.warehouse?.description || '-',
          productLabel: this.formatProductLabel(candidate),
          qty: 1
        };
        this.notificationService.notice('Товар выбран для просмотра');
      } else if (this.mode === 'COUNT') {
        this.pendingProduct = undefined;
        this.pendingProductBarcode = `product:${product.id}`;
        await this.refreshBalances(product.id);

        const alreadyCounted = Boolean(this.currentCountSessionItem);
        if (!alreadyCounted) {
          this.upsertCurrentCountItem(1);
          this.pushRecentAction({
            mode: 'COUNT',
            cellCode: this.currentCell?.code || '-',
            productLabel: this.formatProductLabel(candidate),
            qty: 1,
            message: `Добавлено вручную в переучет по полке ${this.currentCell?.code || ''}`,
            success: true
          });
          this.scanSeries = {
            barcode: `product:${product.id}`,
            mode: 'COUNT',
            cellCode: this.currentCell?.code || '-',
            productLabel: this.formatProductLabel(candidate),
            qty: 1
          };
        }

        this.notificationService.notice(
          alreadyCounted
            ? 'Товар выбран. Можно печатать наклейку и корректировать факт.'
            : 'Товар добавлен в факт. Можно печатать наклейку и скорректировать количество.'
        );
      } else {
        this.pendingProduct = candidate;
        this.pendingProductBarcode = `product:${product.id}`;
        await this.refreshBalances(product.id);
        this.notificationService.notice(
          this.currentCell?.id
            ? 'Товар выбран. Напечатайте наклейку и отсканируйте её для размещения.'
            : 'Товар выбран. Теперь можно печатать наклейку и перейти к полке.'
        );
      }

      this.closeResolutionSheet();
    } catch (error) {
      this.handleOperationError(error, 'Не удалось выбрать товар');
    } finally {
      this.loading = false;
    }
  }

  async pickCandidate(item: BarcodeResolvedProductCandidate) {
    if (!this.pendingUnknownBarcode || this.loading) return;

    this.loading = true;
    try {
      await this.consumeResolvedProduct(this.pendingUnknownBarcode, item);
    } catch (error) {
      this.handleOperationError(error);
    } finally {
      this.loading = false;
    }
  }

  async pickTecDocCandidate(item: BarcodeTecDocCandidate) {
    const barcode = this.pendingUnknownBarcode;
    if (!barcode || !this.warehouse?.id || this.tecdocCandidateLoading) {
      return;
    }

    this.tecdocCandidateLoading = true;
    this.tecdocCandidateError = '';
    this.loading = true;
    try {
      const resolution = await firstValueFrom(
        this.warehouseService.resolveBarcode({
          barcode,
          warehouseId: this.warehouse.id,
          context: 'scanner',
          tecdocCandidate: {
            dataSupplierId: item.dataSupplierId,
            articleNumber: item.articleNumber
          }
        })
      );
      await this.handleBarcodeResolution(barcode, resolution);
    } catch (error) {
      this.tecdocCandidateError = extractApiErrorMessage(
        error,
        'Не удалось создать или привязать товар из TecDoc. Повторите выбор.'
      );
      this.notificationService.warning(this.tecdocCandidateError);
      this.vibrateError();
    } finally {
      this.loading = false;
      this.tecdocCandidateLoading = false;
      this.persistSession();
    }
  }

  closeResolutionSheet() {
    this.pendingUnknownBarcode = '';
    this.candidates = [];
    this.tecdocCandidates = [];
    this.tecdocCandidateError = '';
    this.searchResults = [];
    this.manualProductSearchOpen = false;
    this.unknownSearchControl.setValue('');
    this.persistSession();
    this.focusCaptureInput();
  }

  async finishCountSession(andSwitchToPendingCell = false) {
    if (this.loading || this.isReadOnlyStocktakingDoc || !this.currentCell?.id || !this.warehouse?.id) {
      return;
    }

    this.loading = true;
    try {
      if (this.isStocktakingDocMode && this.stocktakingDocId) {
        this.stocktakingDoc = await firstValueFrom(
          this.stocktakingV2Service.submitShelf({
            docId: this.stocktakingDocId,
            cellId: this.currentCell.id,
            items: this.countSessionItems.map((item) => ({
              productId: item.product.id,
              qty: item.qty
            }))
          })
        );
      } else {
        await firstValueFrom(
          this.warehouseService.countShelfStock({
            warehouseId: this.warehouse.id,
            cellId: this.currentCell.id,
            items: this.countSessionItems.map((item) => ({
              productId: item.product.id,
              qty: item.qty
            })),
            sourceType: 'warehouse_scanner'
          })
        );
      }

      const totalQty = this.countSessionItems.reduce((sum, item) => sum + item.qty, 0);
      const finishedCell = this.currentCell;
      this.pushRecentAction({
        mode: 'COUNT',
        cellCode: finishedCell.code,
        productLabel: `${this.countSessionItems.length} поз.`,
        qty: totalQty,
        message: `Переучет полки ${finishedCell.code} сохранен`,
        success: true
      });
      this.vibrateSuccess();
      this.notificationService.notice(`Полка ${finishedCell.code} сохранена`);

      this.countSessionItems = [];
      this.countEditingProductId = undefined;
      this.countQtyControl.setValue(1);
      this.scanSeries = undefined;

      if (this.currentProduct?.id) {
        await this.refreshBalances(this.currentProduct.id);
      }

      if (andSwitchToPendingCell && this.pendingCellSwitch) {
        const nextCell = this.pendingCellSwitch;
        this.pendingCellSwitch = undefined;
        if (this.isStocktakingDocMode) {
          this.currentCell = undefined;
        }
        await this.activateCell(nextCell);
      } else {
        if (this.isStocktakingDocMode) {
          this.currentCell = undefined;
        }
        this.pendingCellSwitch = undefined;
        this.focusCaptureInput();
      }
    } catch (error) {
      this.handleOperationError(error, 'Не удалось завершить переучет полки');
    } finally {
      this.loading = false;
      this.persistSession();
    }
  }

  async discardCountSession(andSwitchToPendingCell = false) {
    if (this.loading || this.isReadOnlyStocktakingDoc) return;
    const discardedCell = this.currentCell;

    if (this.isStocktakingDocMode && this.stocktakingDocId && discardedCell?.id) {
      this.loading = true;
      try {
        this.stocktakingDoc = await firstValueFrom(
          this.stocktakingV2Service.discardShelf({
            docId: this.stocktakingDocId,
            cellId: discardedCell.id
          })
        );
      } catch (error) {
        this.loading = false;
        this.handleOperationError(error, 'Не удалось сбросить текущую полку');
        return;
      } finally {
        this.loading = false;
      }
    }

    this.countSessionItems = [];
    this.countEditingProductId = undefined;
    this.countQtyControl.setValue(1);
    this.scanSeries = undefined;
    if (this.isStocktakingDocMode) {
      this.currentCell = undefined;
    }

    if (andSwitchToPendingCell && this.pendingCellSwitch) {
      const nextCell = this.pendingCellSwitch;
      this.pendingCellSwitch = undefined;
      void this.activateCell(nextCell);
    } else {
      this.pendingCellSwitch = undefined;
      this.focusCaptureInput();
    }

    this.persistSession();
  }

  cancelPendingCellSwitch() {
    this.pendingCellSwitch = undefined;
    this.persistSession();
    this.focusCaptureInput();
  }

  adjustCurrentCount(delta: number) {
    const item = this.currentCountSessionItem;
    if (!item) {
      if (!this.currentProduct?.id || delta <= 0) {
        return;
      }

      this.upsertCurrentCountItem(this.round(delta));
      return;
    }

    this.adjustCountItem(item, delta);
  }

  startCurrentCountEdit() {
    const item = this.currentCountSessionItem;
    if (!item) {
      if (!this.currentProduct?.id) {
        return;
      }

      this.countEditingProductId = this.currentProduct.id;
      this.countQtyControl.setValue(this.currentScannedQty);
      setTimeout(() => this.countQtyInput?.nativeElement.focus(), 0);
      return;
    }

    this.startCountItemEdit(item);
  }

  applyCurrentCountEdit() {
    const item = this.currentCountSessionItem;
    if (!item) {
      const qty = Math.max(0, this.round(Number(this.countQtyControl.value || 0)));
      if (qty > 0) {
        this.upsertCurrentCountItem(qty);
      }
      this.countEditingProductId = undefined;
      this.countQtyControl.setValue(1);
      this.focusCaptureInput();
      return;
    }

    this.applyCountItemEdit(item);
  }

  alignCurrentCountToSystem() {
    if (!this.currentProduct?.id) {
      return;
    }

    const expectedQty = this.currentExpectedShelfQty;
    const item = this.currentCountSessionItem;

    if (item) {
      this.setCountItemQty(item, expectedQty);
    } else if (expectedQty > 0) {
      this.countSessionItems = [
        {
          barcode: `product:${this.currentProduct.id}`,
          product: this.currentProduct,
          qty: expectedQty,
          lastScannedAt: Date.now()
        },
        ...this.countSessionItems
      ].sort((left, right) => right.lastScannedAt - left.lastScannedAt);
      this.persistSession();
    } else {
      return;
    }

    this.countEditingProductId = undefined;
    this.countQtyControl.setValue(1);
    this.notificationService.notice(`Факт выровнен по системе: ${expectedQty} шт.`);
    this.focusCaptureInput();
  }

  adjustCountItem(item: ScannerCountItem, delta: number) {
    const nextQty = Math.max(0, this.round(item.qty + delta));
    this.setCountItemQty(item, nextQty);
  }

  startCountItemEdit(item: ScannerCountItem) {
    this.countEditingProductId = item.product.id;
    this.countQtyControl.setValue(item.qty);
    setTimeout(() => this.countQtyInput?.nativeElement.focus(), 0);
  }

  applyCountItemEdit(item: ScannerCountItem) {
    const qty = Math.max(0, this.round(Number(this.countQtyControl.value || 0)));
    this.setCountItemQty(item, qty);
    this.countEditingProductId = undefined;
    this.countQtyControl.setValue(1);
    this.focusCaptureInput();
  }

  cancelCountItemEdit() {
    this.countEditingProductId = undefined;
    this.countQtyControl.setValue(1);
    this.focusCaptureInput();
  }

  getBalanceLabel(row: ProductCellBalance) {
    if (row.type === 'REAL') {
      return row.code;
    }
    if (row.type === 'BUFFER') {
      return 'Буфер размещения';
    }
    return 'Неподтвержденное снятие';
  }

  trackByAction(_: number, item: ScannerRecentAction) {
    return item.id;
  }

  trackByCandidate(_: number, item: BarcodeResolvedProductCandidate) {
    return item.id;
  }

  trackByProduct(_: number, item: Product) {
    return item.id;
  }

  trackByCountItem(_: number, item: ScannerCountItem) {
    return item.product.id;
  }

  private round(value: number): number {
    return Math.round(Number(value || 0) * 100) / 100;
  }

  private normalizeQtyValue(value: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return 1;
    }

    return Math.max(1, this.round(numeric));
  }

  private async processScan(raw: string) {
    if (!this.warehouse?.id) {
      return this.notificationService.warning('Сначала выберите склад');
    }

    const code = String(raw || '').trim();
    if (!code) {
      return;
    }

    this.loading = true;
    try {
      if (this.mode === 'INFO' && code.startsWith('!')) {
        this.notificationService.warning('В режиме Инфо сканируйте штрихкод или ID товара.');
        return;
      }

      if (code.startsWith('!')) {
        await this.handleCellScan(code);
      } else {
        await this.handleProductScan(code);
      }
    } catch (error) {
      this.handleOperationError(error);
    } finally {
      this.loading = false;
      this.captureControl.setValue('');
      this.persistSession();
      this.focusCaptureInput();
    }
  }

  private async handleCellScan(raw: string) {
    const resolution = await firstValueFrom(
      this.warehouseService.resolveBarcode({
        barcode: raw,
        warehouseId: this.warehouse!.id,
        context: 'scanner'
      })
    );

    if (resolution.type === 'cell_match') {
      await this.activateCell(resolution.cell);
      return;
    }

    const created = await firstValueFrom(
      this.warehouseService.createCell({
        warehouseId: this.warehouse!.id,
        code: raw
      })
    );

    this.notificationService.notice(`Создана полка ${created.code}`);
    await this.activateCell(created);
    setTimeout(() => this.openCellLabelDialog(created), 0);
  }

  private openCellLabelDialog(cell: WarehouseCell) {
    const dialogRef = this.dialog.open(PrintCellComponent);
    dialogRef.componentInstance.selectCell(cell);
  }

  private async activateCell(cell: WarehouseCell) {
    if (
      this.currentCell?.id &&
      this.currentCell.id !== cell.id &&
      ((this.mode === 'COUNT' && this.countSessionItems.length) || this.isStocktakingDocMode)
    ) {
      this.pendingCellSwitch = cell;
      this.persistSession();
      return;
    }

    if (
      this.isStocktakingDocMode &&
      this.stocktakingDocId &&
      (!this.currentCell?.id || this.currentCell.id !== cell.id)
    ) {
      await this.startStocktakingShelfSession(cell);
    }

    this.currentCell = cell;
    this.pendingCellSwitch = undefined;
    this.notificationService.notice(`Активна полка ${cell.code}`);

    if (this.pendingProduct) {
      const pendingBarcode = this.pendingProductBarcode || `product:${this.pendingProduct.id}`;
      const product = this.pendingProduct;
      this.pendingProduct = undefined;
      this.pendingProductBarcode = '';
      await this.processResolvedProduct(pendingBarcode, product, false);
      return;
    }

    this.persistSession();
    this.focusCaptureInput();
  }

  private async startStocktakingShelfSession(cell: WarehouseCell) {
    if (!this.stocktakingDocId) {
      return;
    }

    const hasLocalUnsavedSession = Boolean(
      this.currentCell?.id === cell.id && this.countSessionItems.length
    );
    const session = await firstValueFrom(
      this.stocktakingV2Service.startShelfSession({
        docId: this.stocktakingDocId,
        cellId: cell.id
      })
    );
    this.stocktakingDoc = session.doc;

    if (!hasLocalUnsavedSession || session.reopened) {
      this.countSessionItems = this.mapStocktakingSessionItems(session.items);
      this.countEditingProductId = undefined;
      this.countQtyControl.setValue(1);
      this.scanSeries = undefined;
    }

    if (session.reopened) {
      this.notificationService.notice(`Полка ${cell.code} переоткрыта`);
    }
  }

  private mapStocktakingSessionItems(items: StocktakingV2ShelfSessionItem[]): ScannerCountItem[] {
    return (Array.isArray(items) ? items : [])
      .map((item) => ({
        barcode: item.barcode || `product:${item.productId}`,
        product: item.product,
        qty: Number(item.qty || 0),
        lastScannedAt: Date.now()
      }))
      .filter((item) => item.product?.id && item.qty > 0);
  }

  private async handleProductScan(raw: string) {
    const remembered = this.sessionMemory.get(raw);
    if (remembered) {
      await this.processResolvedProduct(raw, remembered, false);
      return;
    }

    const resolution = await firstValueFrom(
      this.warehouseService.resolveBarcode({
        barcode: raw,
        warehouseId: this.warehouse!.id,
        context: 'scanner'
      })
    );

    await this.handleBarcodeResolution(raw, resolution);
  }

  private async handleBarcodeResolution(raw: string, resolution: BarcodeResolution) {
    if (resolution.type === 'single_product_match') {
      await this.consumeResolvedProduct(raw, resolution.product);
      return;
    }

    this.pendingUnknownBarcode = raw;
    this.searchResults = [];
    this.unknownSearchControl.setValue('');
    this.candidates = [];
    this.tecdocCandidates = [];
    this.tecdocCandidateError = '';

    if (resolution.type === 'multi_product_match') {
      this.candidates = resolution.candidates;
      this.notificationService.notice('Штрихкод найден у нескольких товаров');
      this.persistSession();
      return;
    }

    if (resolution.type === 'tecdoc_multi_match') {
      this.tecdocCandidates = resolution.candidates;
      this.notificationService.notice('Штрихкод найден в TecDoc. Выберите товар');
      this.persistSession();
      return;
    }

    if (resolution.type === 'tecdoc_materialization_error') {
      this.tecdocCandidateError =
        String(resolution.message || '').trim() ||
        'Не удалось создать или привязать товар из TecDoc. Выберите локальный товар.';
      this.notificationService.warning(this.tecdocCandidateError);
      setTimeout(() => this.unknownSearchInput?.nativeElement.focus(), 0);
      this.persistSession();
      return;
    }

    this.notificationService.warning('Код не найден, выберите товар для привязки');
    setTimeout(() => this.unknownSearchInput?.nativeElement.focus(), 0);
    this.persistSession();
  }

  private async consumeResolvedProduct(
    barcode: string,
    product: BarcodeResolvedProductCandidate,
    remember = true
  ) {
    if (remember) {
      this.sessionMemory.set(barcode, product);
    }

    this.closeResolutionSheet();
    await this.processResolvedProduct(barcode, product, true);
  }

  private async processResolvedProduct(
    barcode: string,
    product: BarcodeResolvedProductCandidate,
    refreshBeforePending: boolean
  ) {
    this.currentProduct = product;
    void this.hydrateCurrentProductDetails(product.id);

    if (this.mode === 'INFO') {
      this.pendingProduct = undefined;
      this.pendingProductBarcode = '';
      await this.refreshBalances(product.id);
      this.pushRecentAction({
        mode: 'INFO',
        cellCode: this.warehouse?.description || '-',
        productLabel: this.formatProductLabel(product),
        qty: 1,
        message: 'Открыта карточка товара',
        success: true
      });
      this.scanSeries = {
        barcode,
        mode: 'INFO',
        cellCode: this.warehouse?.description || '-',
        productLabel: this.formatProductLabel(product),
        qty: 1
      };
      this.notificationService.notice('Информация о товаре обновлена');
      this.vibrateSuccess();
      this.persistSession();
      return;
    }

    if (!this.currentCell?.id) {
      this.pendingProduct = product;
      this.pendingProductBarcode = barcode;
      if (refreshBeforePending) {
        await this.refreshBalances(product.id);
      }
      this.notificationService.notice('Теперь сканируйте полку');
      this.persistSession();
      return;
    }

    this.pendingProduct = undefined;
    this.pendingProductBarcode = '';

    if (this.mode === 'COUNT') {
      this.incrementCountItem(product, barcode);
      await this.refreshBalances(product.id);
      this.persistSession();
      return;
    }

    await this.commitUnitAction(product, barcode);
    this.persistSession();
  }

  private async commitUnitAction(product: BarcodeResolvedProductCandidate, barcode: string) {
    if (!this.warehouse?.id || !this.currentCell?.id) {
      return;
    }

    const qty = this.activeQtyOverride;
    this.currentProduct = product;
    void this.hydrateCurrentProductDetails(product.id);

    if (this.mode === 'PLACE') {
      this.productBalances = await firstValueFrom(
        this.warehouseService.placeStock({
          warehouseId: this.warehouse.id,
          productId: product.id,
          toCellId: this.currentCell.id,
          qty,
          sourceType: 'warehouse_scanner'
        })
      );

      this.registerScanSeries(barcode, product, qty);
      this.pushRecentAction({
        mode: 'PLACE',
        cellCode: this.currentCell.code,
        productLabel: this.formatProductLabel(product),
        qty,
        message: `Размещено на полку ${this.currentCell.code}`,
        success: true
      });
      this.notificationService.notice(`Размещено: ${qty} шт.`);
      this.vibrateSuccess();
      this.resetQtyOverride();
      return;
    }

    this.productBalances = await firstValueFrom(
      this.warehouseService.pickStock({
        warehouseId: this.warehouse.id,
        productId: product.id,
        fromCellId: this.currentCell.id,
        qty,
        sourceType: 'warehouse_scanner'
      })
    );

    this.registerScanSeries(barcode, product, qty);
    this.pushRecentAction({
      mode: 'PICK',
      cellCode: this.currentCell.code,
      productLabel: this.formatProductLabel(product),
      qty,
      message: `Снято с полки ${this.currentCell.code}`,
      success: true
    });
    this.notificationService.notice(`Снято: ${qty} шт.`);
    this.vibrateSuccess();
    this.resetQtyOverride();
  }

  private incrementCountItem(product: BarcodeResolvedProductCandidate, barcode: string) {
    const existing = this.countSessionItems.find((item) => item.product.id === product.id);

    if (existing) {
      existing.qty = this.round(existing.qty + 1);
      existing.lastScannedAt = Date.now();
      existing.barcode = barcode;
    } else {
      this.countSessionItems = [
        {
          barcode,
          product,
          qty: 1,
          lastScannedAt: Date.now()
        },
        ...this.countSessionItems
      ];
    }

    this.countSessionItems = [...this.countSessionItems].sort(
      (left, right) => right.lastScannedAt - left.lastScannedAt
    );

    this.registerScanSeries(barcode, product, 1);
    this.pushRecentAction({
      mode: 'COUNT',
      cellCode: this.currentCell?.code || '-',
      productLabel: this.formatProductLabel(product),
      qty: 1,
      message: `Учтено на полке ${this.currentCell?.code || ''}`,
      success: true
    });
    this.vibrateSuccess();
  }

  private setCountItemQty(item: ScannerCountItem, qty: number) {
    const nextQty = this.round(qty);
    if (nextQty <= 0) {
      this.countSessionItems = this.countSessionItems.filter((row) => row.product.id !== item.product.id);
      if (this.countEditingProductId === item.product.id) {
        this.countEditingProductId = undefined;
        this.countQtyControl.setValue(1);
      }
      this.persistSession();
      return;
    }

    item.qty = nextQty;
    item.lastScannedAt = Date.now();
    this.countSessionItems = [...this.countSessionItems].sort(
      (left, right) => right.lastScannedAt - left.lastScannedAt
    );
    this.persistSession();
  }

  private upsertCurrentCountItem(qty: number) {
    if (!this.currentProduct?.id) {
      return;
    }

    const nextQty = Math.max(0, this.round(qty));
    const existing = this.currentCountSessionItem;
    if (existing) {
      this.setCountItemQty(existing, nextQty);
      return;
    }

    if (nextQty <= 0) {
      return;
    }

    this.countSessionItems = [
      {
        barcode: `product:${this.currentProduct.id}`,
        product: this.currentProduct,
        qty: nextQty,
        lastScannedAt: Date.now()
      },
      ...this.countSessionItems
    ].sort((left, right) => right.lastScannedAt - left.lastScannedAt);
    this.persistSession();
  }

  private pushRecentAction(payload: Omit<ScannerRecentAction, 'id' | 'createdAt'>) {
    this.recentActions = [
      {
        ...payload,
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        createdAt: Date.now()
      },
      ...this.recentActions
    ].slice(0, 8);
    this.persistSession();
  }

  private registerScanSeries(
    barcode: string,
    product: BarcodeResolvedProductCandidate,
    qty: number
  ) {
    const productLabel = this.formatProductLabel(product);
    const cellCode = this.currentCell?.code || '-';

    if (
      this.scanSeries &&
      this.scanSeries.barcode === barcode &&
      this.scanSeries.mode === this.mode &&
      this.scanSeries.cellCode === cellCode
    ) {
      this.scanSeries = {
        ...this.scanSeries,
        qty: this.round(this.scanSeries.qty + qty)
      };
      return;
    }

    this.scanSeries = {
      barcode,
      mode: this.mode,
      cellCode,
      productLabel,
      qty
    };
  }

  private formatProductLabel(product: BarcodeResolvedProductCandidate) {
    return `${product.brand.description} ${product.article} ${product.description}`.trim();
  }

  private mapProductToCandidate(product: Product): BarcodeResolvedProductCandidate {
    const key = String(this.warehouse?.id || '');
    return {
      id: product.id,
      article: product.article,
      description: product.description,
      retail: Number(product.retail || 0),
      price: Number(product.price || 0),
      brand: {
        id: product.brand.id,
        description: product.brand.description
      },
      cellHints: product.stockSummary?.[key]?.recommendedCells || []
    };
  }

  private applyRealtimeProductUpdate(product: Product) {
    if (!this.currentProduct) {
      return;
    }

    this.currentProductDetails = product;
    const key = String(this.warehouse?.id || '');
    this.currentProduct = {
      ...this.currentProduct,
      id: product.id,
      article: product.article,
      description: product.description,
      retail: Number(product.retail || 0),
      price: Number(product.price || 0),
      brand: {
        id: product.brand?.id || this.currentProduct.brand.id,
        description: product.brand?.description || this.currentProduct.brand.description
      },
      cellHints: product.stockSummary?.[key]?.recommendedCells || []
    };

    if (this.warehouse?.id && product.id) {
      void this.refreshBalances(product.id);
    }

    this.persistSession();
  }

  private async refreshBalances(productId: number) {
    if (!this.warehouse?.id) return;

    this.productBalances = await firstValueFrom(
      this.warehouseService.getCellBalances({
        warehouseId: this.warehouse.id,
        productId,
        includeVirtual: true
      })
    );
  }

  private async hydrateCurrentProductDetails(productId?: number) {
    if (!productId) {
      this.currentProductDetails = undefined;
      return;
    }

    if (this.currentProductDetails?.id === productId) {
      return;
    }

    this.currentProductDetails = undefined;

    try {
      this.currentProductDetails = await firstValueFrom(this.productService.getOne(productId));
    } catch (error) {
      this.currentProductDetails = undefined;
    }
  }

  private handleModeChange(mode: ScannerMode) {
    if (!this.canUseAdhocCount && mode === 'COUNT') {
      this.modeControl.setValue('PLACE', { emitEvent: false });
      this.notificationService.warning('Режим переучета доступен только администратору');
      return;
    }

    if (this.isStocktakingDocMode && mode !== 'COUNT') {
      this.modeControl.setValue('COUNT', { emitEvent: false });
      return;
    }

    this.closeResolutionSheet();
    this.pendingCellSwitch = undefined;
    this.showQtyOverride = false;
    this.scanBuffer = '';
    this.scanSeries = undefined;
    this.pendingProduct = undefined;
    this.pendingProductBarcode = '';

    if (mode !== 'COUNT') {
      this.countSessionItems = [];
      this.countEditingProductId = undefined;
      this.countQtyControl.setValue(1);
    }

    this.persistSession();
    this.focusCaptureInput();
  }

  private async handleWarehouseChange(warehouse?: Warehouse) {
    this.showWarehouseSelector = false;

    if (this.isStocktakingDocMode) {
      if (this.stocktakingDoc?.warehouse?.id && warehouse?.id !== this.stocktakingDoc.warehouse.id) {
        this.warehouseControl.setValue(this.stocktakingDoc.warehouse, { emitEvent: false });
      }
      this.focusCaptureInput();
      return;
    }

    const nextWarehouseId = warehouse?.id;
    if (!nextWarehouseId) {
      return;
    }

    if (this.lastWarehouseId === undefined) {
      this.lastWarehouseId = nextWarehouseId;
      this.persistSession();
      this.focusCaptureInput();
      return;
    }

    if (this.lastWarehouseId === nextWarehouseId) {
      this.persistSession();
      this.focusCaptureInput();
      return;
    }

    this.lastWarehouseId = nextWarehouseId;
    this.currentCell = undefined;
    this.pendingCellSwitch = undefined;
    this.pendingProduct = undefined;
    this.pendingProductBarcode = '';
    this.countSessionItems = [];
    this.countEditingProductId = undefined;
    this.productBalances = [];
    this.scanSeries = undefined;
    this.recentActions = [];

    if (this.currentProduct?.id) {
      await this.refreshBalances(this.currentProduct.id);
    }

    this.persistSession();
    this.focusCaptureInput();
  }

  private async restoreSessionSnapshot() {
    const snapshot = this.readSessionSnapshot();
    if (!snapshot) {
      return;
    }

    if (snapshot.mode && !this.isStocktakingDocMode) {
      this.modeControl.setValue(
        !this.canUseAdhocCount && snapshot.mode === 'COUNT' ? 'PLACE' : snapshot.mode,
        { emitEvent: false }
      );
    }
    if (snapshot.currentCell && !this.isStocktakingDocMode) {
      this.currentCell = snapshot.currentCell;
    }
    if (snapshot.currentProduct) {
      this.currentProduct = snapshot.currentProduct;
      void this.hydrateCurrentProductDetails(snapshot.currentProduct.id);
    }
    if (snapshot.pendingProduct) {
      this.pendingProduct = snapshot.pendingProduct;
    }
    this.pendingProductBarcode = String(snapshot.pendingProductBarcode || '');
    this.countSessionItems = Array.isArray(snapshot.countSessionItems) ? snapshot.countSessionItems : [];
    this.recentActions = Array.isArray(snapshot.recentActions) ? snapshot.recentActions : [];
    this.appliedQtyOverride = this.normalizeQtyValue(snapshot.qtyOverride || 1);
    this.qtyControl.setValue(this.appliedQtyOverride);

    if (this.isStocktakingDocMode && !this.currentCell?.id) {
      this.countSessionItems = [];
    }

    if (snapshot.warehouseId && !this.isStocktakingDocMode) {
      await this.applyWarehouseFromId(snapshot.warehouseId);
    }

    if (this.currentProduct?.id && this.warehouse?.id) {
      await this.refreshBalances(this.currentProduct.id);
    }

    this.persistSession();
    this.focusCaptureInput();
  }

  private readSessionSnapshot(): ScannerSessionSnapshot | null {
    try {
      const raw = globalThis.sessionStorage?.getItem(this.sessionStorageKey);
      if (!raw) return null;
      return JSON.parse(raw) as ScannerSessionSnapshot;
    } catch (error) {
      return null;
    }
  }

  private persistSession() {
    const snapshot: ScannerSessionSnapshot = {
      warehouseId: this.warehouse?.id || null,
      mode: this.mode,
      currentCell: this.currentCell || null,
      currentProduct: this.currentProduct || null,
      pendingProduct: this.pendingProduct || null,
      pendingProductBarcode: this.pendingProductBarcode || null,
      qtyOverride: this.activeQtyOverride,
      countSessionItems: this.countSessionItems,
      recentActions: this.recentActions
    };

    try {
      globalThis.sessionStorage?.setItem(this.sessionStorageKey, JSON.stringify(snapshot));
    } catch (error) {
      return;
    }
  }

  private vibrateSuccess() {
    globalThis.navigator?.vibrate?.(30);
  }

  private vibrateError() {
    globalThis.navigator?.vibrate?.([70, 40, 70]);
  }

  private handleOperationError(error: unknown, fallbackMessage = 'Не удалось выполнить операцию') {
    const message =
      typeof error === 'string'
        ? error
        : error instanceof Error
          ? error.message
          : fallbackMessage;
    this.pushRecentAction({
      mode: this.mode,
      cellCode: this.currentCell?.code || '-',
      productLabel: this.currentProduct ? this.formatProductLabel(this.currentProduct) : 'Товар не выбран',
      qty: this.mode === 'COUNT' ? 1 : this.activeQtyOverride,
      message,
      success: false
    });
    this.vibrateError();
    this.notificationService.warning(message || fallbackMessage);
    this.focusCaptureInput();
  }

  private async applyRouteContext(payload: {
    warehouseId?: number;
    productId?: number;
    cellId?: number;
    mode?: ScannerMode;
  }) {
    if (payload.cellId) {
      this.pendingRouteCellId = Number(payload.cellId || 0) || undefined;
    }

    if (this.isStocktakingDocMode) {
      this.modeControl.setValue('COUNT', { emitEvent: false });
      if (this.pendingRouteCellId && this.stocktakingDoc?.id) {
        await this.applyRouteCellFromId(this.pendingRouteCellId);
      }
      return;
    }

    if (payload.mode) {
      this.modeControl.setValue(!this.canUseAdhocCount && payload.mode === 'COUNT' ? 'PLACE' : payload.mode);
    }

    if (payload.warehouseId) {
      await this.applyWarehouseFromId(payload.warehouseId);
    }

    if (payload.productId) {
      await this.applyProductFromRoute(payload.productId);
    }
  }

  private async applyRouteCellFromId(cellId: number) {
    if (!this.stocktakingDoc?.id || !cellId) {
      return;
    }

    const shelf = this.stocktakingDoc.shelves.find((row) => Number(row.cellId) === Number(cellId));
    if (!shelf) {
      this.notificationService.warning('Полка не найдена в документе переучета');
      return;
    }

    await this.activateCell({
      id: Number(shelf.cellId),
      warehouseId: this.stocktakingDoc.warehouse.id,
      code: shelf.cellCode,
      type: 'REAL',
      isActive: true,
      sortOrder: 0
    });
  }

  private async applyWarehouseFromId(warehouseId: number) {
    const currentWarehouse = this.warehouse;
    if (currentWarehouse?.id === warehouseId) {
      return;
    }

    const warehouses = await firstValueFrom(this.warehouseService.getAllList());
    const selected = warehouses.find((row) => row.id === warehouseId);
    if (selected) {
      this.warehouseControl.setValue(selected);
      this.lastWarehouseId = selected.id;
    }
  }

  private async applyProductFromRoute(productId: number) {
    const product = await firstValueFrom(this.productService.getOne(productId));
    const candidate = this.mapProductToCandidate(product);
    this.currentProduct = candidate;
    this.currentProductDetails = product;
    if (this.mode === 'INFO') {
      this.pendingProduct = undefined;
      this.pendingProductBarcode = '';
    } else {
      this.pendingProduct = candidate;
      this.pendingProductBarcode = `product:${product.id}`;
    }
    await this.refreshBalances(product.id);
    this.persistSession();
    this.focusCaptureInput();
  }

  private async handleRouteDocContext(docId: number) {
    const nextDocId = docId > 0 ? docId : undefined;
    const wasDocMode = this.isStocktakingDocMode;

    if (this.stocktakingDocId === nextDocId) {
      return;
    }

    this.stopStocktakingHeartbeat();
    this.stocktakingDocId = nextDocId;

    if (!nextDocId) {
      this.stocktakingDoc = undefined;
      this.pendingRouteCellId = undefined;
      this.labelPrinterWarning = '';
      if (wasDocMode) {
        this.modeControl.enable({ emitEvent: false });
        this.warehouseControl.enable({ emitEvent: false });
        if (!this.isAdminUser && this.modeControl.value === 'COUNT') {
          this.modeControl.setValue('PLACE', { emitEvent: false });
        }
        this.currentCell = undefined;
        this.pendingCellSwitch = undefined;
        this.countSessionItems = [];
        this.scanSeries = undefined;
      }
      this.focusCaptureInput(true);
      return;
    }

    this.modeControl.setValue('COUNT', { emitEvent: false });
    this.modeControl.disable({ emitEvent: false });
    this.warehouseControl.disable({ emitEvent: false });
    this.showQtyOverride = false;
    this.pendingCellSwitch = undefined;
    this.countSessionItems = [];
    this.scanSeries = undefined;

    await this.loadStocktakingDoc(nextDocId, true);
    await this.restoreSessionSnapshot();
    if (this.pendingRouteCellId) {
      await this.applyRouteCellFromId(this.pendingRouteCellId);
    }

    if (this.currentProduct?.id && this.warehouse?.id) {
      await this.refreshBalances(this.currentProduct.id);
    }

    this.startStocktakingHeartbeat();
    this.focusCaptureInput(true);
  }

  private async loadStocktakingDoc(docId: number, joinParticipant = false) {
    this.stocktakingDoc = joinParticipant
      ? await firstValueFrom(this.stocktakingV2Service.joinParticipant({ docId }))
      : await firstValueFrom(this.stocktakingV2Service.getOne(docId));

    this.warehouseControl.setValue(this.stocktakingDoc.warehouse, { emitEvent: false });
    this.lastWarehouseId = this.stocktakingDoc.warehouse.id;

    const activeParticipant = this.stocktakingDoc.participants.find(
      (participant) => participant.userId === this.currentUserId
    );
    const activeCellId = Number(activeParticipant?.activeCellId || 0);
    if (activeCellId > 0) {
      const activeShelf = this.stocktakingDoc.shelves.find((shelf) => shelf.cellId === activeCellId);
      this.currentCell = {
        id: activeCellId,
        warehouseId: this.stocktakingDoc.warehouse.id,
        code: activeParticipant?.activeCellCode || activeShelf?.cellCode || `#${activeCellId}`,
        type: 'REAL',
        isActive: true,
        sortOrder: 0
      };
    } else {
      this.currentCell = undefined;
    }

    if (this.stocktakingDoc.status !== 'COUNTING') {
      this.notificationService.warning('Документ уже не находится в режиме пересчета');
    } else if (this.currentCell?.id) {
      await this.startStocktakingShelfSession(this.currentCell);
    }

    await this.checkStocktakingLabelPrinter();
  }

  private async checkStocktakingLabelPrinter() {
    const warehouseId = this.stocktakingDoc?.warehouse?.id;
    if (!this.isStocktakingDocMode || !warehouseId) {
      this.labelPrinterWarning = '';
      return;
    }

    try {
      const targets = await firstValueFrom(this.computerService.getPrinterTargets('label', warehouseId));
      this.labelPrinterWarning = targets.length
        ? ''
        : `Нет онлайн-принтера этикеток для склада ${this.stocktakingDoc?.warehouse?.description || `#${warehouseId}`}. Проверьте настройки принтеров склада.`;
    } catch (error) {
      this.labelPrinterWarning = `Не удалось проверить принтер этикеток склада ${this.stocktakingDoc?.warehouse?.description || `#${warehouseId}`}.`;
    }
  }

  private startStocktakingHeartbeat() {
    if (!this.isStocktakingDocMode || !this.stocktakingDocId) {
      return;
    }

    this.stopStocktakingHeartbeat();
    this.sendStocktakingHeartbeat();
    this.heartbeatTimer = globalThis.window?.setInterval(() => {
      this.sendStocktakingHeartbeat();
    }, 30000);
  }

  private sendStocktakingHeartbeat() {
    if (!this.isStocktakingDocMode || !this.stocktakingDocId || this.loading) {
      return;
    }

    this.stocktakingV2Service.heartbeat({ docId: this.stocktakingDocId }).subscribe({
      error: () => undefined
    });
  }

  // На ТСД при погасшем экране/уходе в фон браузер замораживает setInterval, heartbeat
  // перестает идти, и серверный лок полки протухает по таймауту. На пробуждении сразу
  // пингуем сервер, чтобы продлить лок до того, как менеджер продолжит работу.
  @HostListener('document:visibilitychange')
  @HostListener('window:focus')
  @HostListener('window:online')
  onStocktakingActivityResumed() {
    if (globalThis.document?.visibilityState === 'hidden') {
      return;
    }
    this.sendStocktakingHeartbeat();
  }

  private stopStocktakingHeartbeat() {
    if (this.heartbeatTimer) {
      globalThis.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  focusCaptureInput(force = false) {
    if (!force && this.isCapturePaused) {
      return;
    }

    setTimeout(() => {
      if (!this.captureInput?.nativeElement) {
        return;
      }
      const input = this.captureInput.nativeElement;
      input.focus({ preventScroll: true });
      const value = String(input.value || '');
      const position = value.length;
      input.setSelectionRange(position, position);
    }, 0);
  }

  private cancelProductLabelLongPress() {
    if (!this.productLabelLongPressTimer) {
      return;
    }

    globalThis.clearTimeout(this.productLabelLongPressTimer);
    this.productLabelLongPressTimer = undefined;
  }

  private cancelCellLabelLongPress() {
    if (!this.cellLabelLongPressTimer) {
      return;
    }

    globalThis.clearTimeout(this.cellLabelLongPressTimer);
    this.cellLabelLongPressTimer = undefined;
  }

  private async quickPrintCurrentProductLabel() {
    if (!this.currentProduct?.id) {
      return;
    }

    await this.computerService.printProductLabel({
      productId: this.currentProduct.id,
      quanInPackage: 1,
      quanPrint: 1,
      warehouseId: this.stocktakingPrintWarehouseId,
      labelProfileMode: this.stocktakingLabelProfileMode
    });

    this.notificationService.notice('Этикетка отправлена на печать');
  }

  private async quickPrintCurrentCellLabel() {
    if (!this.currentCell?.code || this.printingCellLabel) {
      return;
    }

    this.printingCellLabel = true;
    try {
      await this.computerService.printCellLabel({
        cell: this.currentCell.code,
        cellCode: this.currentCell.code,
        cellId: this.currentCell.id,
        quan: 1,
        barcodeType: 'CODE128',
        barcodeWidth: 2,
        copies: 1,
        warehouseId: this.currentCell.warehouseId,
        labelProfileMode: 'printer_target',
        target: { warehouseId: this.currentCell.warehouseId, strategy: 'warehouse_online' }
      });

      this.notificationService.notice('Этикетка ячейки отправлена на печать');
    } catch (error) {
      this.handleOperationError(error, 'Не удалось отправить этикетку ячейки на печать');
    } finally {
      this.printingCellLabel = false;
    }
  }

  private printingCellLabel = false;
}
